import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { AbstractControl, FormGroup } from "@angular/forms";
import { ActivatedRoute, ParamMap } from "@angular/router";
import { of, Subject } from "rxjs";
import { catchError, distinctUntilChanged, takeUntil } from "rxjs/operators";

import { LgdFormControlFactory, setEnabled } from "@tylertech/lgd-reactive-forms";
import { getLoggerFor, LogInvocation, LogLevel } from "@tylertech/logging";
import { StringHelper } from "@tylertech/shared-module";
import { ExecutionResult } from "@tylertech/tyler-api-types";

import { Auth0ErrorResponse, LoginView } from "../../../interfaces";
import { isErroredExecutionResult, LoginError, RouteInfoService, SecurityService } from "../../../services";
import { SecurityScreenType } from "../securityScreenType";

@Component({
    selector: "login-form",
    templateUrl: "./loginForm.tmpl.html",
    styleUrls: [
        "./loginForm.component.scss",
    ],
    standalone: false
})
export class LoginFormComponent
    implements OnInit, OnDestroy
{
    private readonly log = getLoggerFor("LoginFormComponent");

    @Input() isDisabled: boolean;
    @Input() showForgotPwLink: boolean;
    @Input() showRememberMe: boolean;

    @Output() protected readonly goto = new EventEmitter<SecurityScreenType>();

    protected readonly destructor$ = new Subject<void>();

    private routeParams: Record<string, string>;
    readonly mainForm: FormGroup;

    // private authKey: string;
    authKeyLogin: boolean;
    error: string;
    loading: boolean;

    constructor(
        private readonly $routeInfoService: RouteInfoService,
        private readonly $securityService: SecurityService,
        activatedRoute: ActivatedRoute,
    )
    {
        activatedRoute.queryParamMap
            .pipe(takeUntil(this.destructor$))
            .subscribe((paramMap: ParamMap) =>
            {
                this.routeParams = paramMap.keys.reduce(
                    (obj: Record<string, string>, key: string) =>
                    {
                        obj[key] = paramMap.get(key);

                        return obj;
                    },
                    {});
            });

        this.mainForm = new FormGroup({
            username: LgdFormControlFactory.text(undefined, true, {}),
            password: LgdFormControlFactory.text(undefined, true, {}),
            rememberMe: LgdFormControlFactory.checkbox(),
        });
    }

    protected get usernameControl(): AbstractControl<string> { return this.mainForm.get("username"); }
    protected get passwordControl(): AbstractControl<string> { return this.mainForm.get("password"); }
    protected get rememberMeControl(): AbstractControl<boolean> { return this.mainForm.get("rememberMe"); }

    private get authKey(): string
    {
        return this.routeParams.K
            ?? this.routeParams.k
            ?? "";
    }

    private get tenant(): string
    {
        return this.$routeInfoService.tenant?.replace(/"/g, "");
    }

    get submitDisabled(): boolean
    {
        return this.isDisabled
            || this.loading
            || (this.isRadius && !this.isSpecialUser);
    }

    get allowForgetPassword(): boolean { return this.showForgotPwLink; }
    get forgotPasswordDisabled(): boolean { return this.isDisabled || this.loading; }
    get isRadius(): boolean {  return this.$routeInfoService.path?.endsWith("/radius") ?? false; }

    private get isSpecialUser(): boolean { return this.usernameControl.value?.startsWith("#"); }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    ngOnInit(): void
    {
        this.usernameControl.valueChanges
            .pipe(
                distinctUntilChanged(),
                takeUntil(this.destructor$))
            .subscribe((newValue: string) =>
            {
                if (newValue && this.isSpecialUser)
                {
                    this.rememberMeControl.setValue(false);
                }

                setEnabled(this.rememberMeControl, !this.isSpecialUser);
            });

        // TODO - why do we ever have quotes in the username value?
        const username = this.$securityService.Username?.replace(/"/g, "");

        this.usernameControl.setValue(username);
        if (username && !this.isSpecialUser)
        {
            this.rememberMeControl.setValue(true);
        }

        setEnabled(this.mainForm, !this.isDisabled);

        if (this.authKey)
        {
            this.loginWithAuthKey();
        }
    }

    @HostListener("keydown", ["$event"])
    onKeydown($event: KeyboardEvent): void
    {
        if ($event.key === "Enter" && !this.submitDisabled)
        {
            this.login();
        }
    }

    protected loginWithAuthKey(): void
    {
        this.authKeyLogin = true;
        this.login();
    }

    @LogInvocation(LogLevel.DEBUG)
    switchToForgotPassword(): void
    {
        this.goto.emit(SecurityScreenType.ForgotPassword);
    }

    @LogInvocation(LogLevel.DEBUG)
    login(): void
    {
        if (!this.authKey && this.mainForm.invalid)
        {
            return;
        }

        this.loading = true;
        setEnabled(this.mainForm, false);

        const credentials: LoginView = {
            Tenant: this.tenant,
            Username: !this.authKey
                ? this.usernameControl.value
                : "",
            Password: !this.authKey
                ? this.passwordControl.value
                : "",
            AuthKey: this.authKey,
            SaveCredentials: !this.isSpecialUser
                && this.rememberMeControl.value,
        };

        this.$securityService.loginWithRedirect(credentials)
            .pipe(
                catchError((result: unknown) =>
                {
                    this.authKeyLogin = false;

                    if (result instanceof LoginError)
                    {
                        const rejectedResponse = result.response;
                        const executionResult = rejectedResponse.body;
                        this.error = this.parseError(executionResult);
                    }
                    else if (isErroredExecutionResult(result))
                    {
                        this.error = this.parseError(result);
                    }

                    this.log.information("error logging in", result);
                    return of(false);
                }))
            .subscribe({
                next: (_success: boolean) =>
                {
                    if (_success)
                    {
                        this.log.debug("successful login");
                    }
                    else
                    {
                        this.log.warning("login failed");
                    }
                },
                complete: () =>
                {
                    setEnabled(this.mainForm, !this.isDisabled);

                    this.loading = false;
                }
            });
    }

    private parseError<T = unknown>(result: ExecutionResult<T>): string
    {
        const message = result.ErrorMessage
            ?? result.ValidationMessage;
        const errorObj = StringHelper.parse<Auth0ErrorResponse>(message);
        return errorObj?.error_description
            ?? message;
    }
}
